import { useState } from 'react';
import { Card, CardContent, PageTitle } from 'shared-components';
import { Button, ButtonIntent } from 'shared-components';
import Routes from '../../app/routes';
import { DateTime } from '../datetime/datetime';
import {
  useGetStagingFilesQuery,
  useDeleteStagingFilesMutation,
  useLazyGetStagingDownloadUrlQuery,
} from './staging.api';
import { IStagedFile } from '@topshots/core-models';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import styles from './staging.module.css';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const Staging = () => {
  const { data: groups, isLoading } = useGetStagingFilesQuery(undefined, {
    pollingInterval: 30000,
  });
  const [deleteStagingFiles] = useDeleteStagingFilesMutation();
  const [getDownloadUrl] = useLazyGetStagingDownloadUrlQuery();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<
    Record<string, Set<string>>
  >({});

  const toggleExpanded = (userId: string) => {
    setExpanded((prev) => {
      const wasExpanded = prev[userId] ?? true;
      return { ...prev, [userId]: !wasExpanded };
    });
  };

  const toggleFileSelection = (userId: string, filename: string) => {
    setSelected((prev) => {
      const userSet = new Set(prev[userId] ?? []);
      if (userSet.has(filename)) {
        userSet.delete(filename);
      } else {
        userSet.add(filename);
      }
      return { ...prev, [userId]: userSet };
    });
  };

  const toggleGroupSelection = (userId: string, files: IStagedFile[]) => {
    setSelected((prev) => {
      const userSet = new Set(prev[userId] ?? []);
      const allSelected = files.every((f) => userSet.has(f.name));
      if (allSelected) {
        return { ...prev, [userId]: new Set() };
      } else {
        return {
          ...prev,
          [userId]: new Set(files.map((f) => f.name)),
        };
      }
    });
  };

  const downloadFile = async (userId: string, filename: string) => {
    try {
      const result = await getDownloadUrl({ filename, userId }).unwrap();
      window.open(result.url, '_blank');
    } catch {
      toast.error('Failed to get download URL');
    }
  };

  const downloadGroup = async (userId: string, files: IStagedFile[]) => {
    for (const file of files) {
      await downloadFile(userId, file.name);
    }
  };

  const deleteSelected = async () => {
    const filesToDelete: { userId: string; filename: string }[] = [];
    for (const [userId, filenames] of Object.entries(selected)) {
      for (const filename of filenames) {
        filesToDelete.push({ userId, filename });
      }
    }
    if (filesToDelete.length === 0) {
      toast.warn('No files selected');
      return;
    }
    if (
      !confirm(
        `Are you sure you want to delete ${filesToDelete.length} file(s)?`
      )
    ) {
      return;
    }
    try {
      const result = await deleteStagingFiles({
        files: filesToDelete,
      }).unwrap();
      toast.success(`Deleted ${result.deleted} file(s)`);
      setSelected({});
    } catch {
      toast.error('Failed to delete files');
    }
  };

  const totalSelected = Object.values(selected).reduce(
    (sum, s) => sum + s.size,
    0
  );

  if (isLoading) {
    return <span>loading...</span>;
  }

  return (
    <>
      <PageTitle
        title="Staging"
        crumbs={[
          { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
          { display: Routes.STAGING.title, link: Routes.STAGING.link },
        ]}
      />

      {(!groups || groups.length === 0) && (
        <Card>
          <CardContent>
            <p>No staged files. Photos uploaded from camera devices will appear here.</p>
          </CardContent>
        </Card>
      )}

      {totalSelected > 0 && (
        <div className={styles.actionBar}>
          <span>{totalSelected} file(s) selected</span>
          <Button intent={ButtonIntent.Negative} onClick={deleteSelected}>
            Delete Selected
          </Button>
        </div>
      )}

      {groups?.map((group) => {
        const isExpanded = expanded[group.userId] !== false;
        const userSelected = selected[group.userId] ?? new Set();
        const allSelected =
          group.files.length > 0 &&
          group.files.every((f) => userSelected.has(f.name));

        return (
          <Card key={group.userId}>
            <div className={styles.groupHeader}>
              <button
                type="button"
                className={styles.groupToggle}
                onClick={() => toggleExpanded(group.userId)}
              >
                {isExpanded ? (
                  <ChevronDownIcon height={18} />
                ) : (
                  <ChevronRightIcon height={18} />
                )}
                <strong>{group.userName}</strong>
                <span className={styles.groupMeta}>
                  {group.files.length} file(s) &middot;{' '}
                  {formatBytes(group.totalSize)}
                </span>
              </button>
              <div className={styles.groupActions}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() =>
                      toggleGroupSelection(group.userId, group.files)
                    }
                  />
                  Select all
                </label>
                <Button
                  onClick={() => downloadGroup(group.userId, group.files)}
                >
                  Download All
                </Button>
              </div>
            </div>

            {isExpanded && (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>File</th>
                    <th>Size</th>
                    <th>Uploaded</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {group.files.map((file) => (
                    <tr key={file.cloudPath}>
                      <td>
                        <input
                          type="checkbox"
                          checked={userSelected.has(file.name)}
                          onChange={() =>
                            toggleFileSelection(group.userId, file.name)
                          }
                        />
                      </td>
                      <td>{file.name}</td>
                      <td>{formatBytes(file.size)}</td>
                      <td>
                        <DateTime d={file.created} />
                      </td>
                      <td>
                        <Button
                          onClick={() =>
                            downloadFile(group.userId, file.name)
                          }
                        >
                          Download
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        );
      })}
    </>
  );
};
